import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import { Button, EmptyState, ErrorState, Modal, Skeleton, TextLink } from '@indipia/ui-core'
import {
  CUST_REQ_MESSAGE, custReqErrorMessage, pickUnsentItems, splitByChecked, type CustReqItem,
  useAddCustReqPhotos, useCreateCustReq, useCustReqList, useCustReqSrcOptions, useDeleteCustReq, useDeleteCustReqPhoto, useUpdateCustReq,
} from '@indipia/shared-features'
import { SCHEDULE_LIST_KEY } from '../../hooks/useScheduleListData'
import { TaskDetailCard } from '../TaskDetailCard'
import { CustReqAdditionalDelivery } from './CustReqAdditionalDelivery'
import { CustReqForm, type CustReqFormValue } from './CustReqForm'
import { CustReqItemRow } from './CustReqItemRow'

// 간트 공종 상세창 "고객 요청" 카드(S16). 위치: 시공파트너 → 자재파트너 → 고객 요청 → 작업 요청 메모 → 선행공종.
// 등록·수정·삭제는 여기서만 한다(리스트 보기는 보기 전용). 등록·수정·삭제·사진 변경이 성공하면 onChanged 로 알려 간트 배지를 다시 읽게 한다.

/** 딥링크 card=cr 앵커 id(K0 계약 5장) */
export const CUST_REQ_CARD_ANCHOR_ID = 'cust-req-card'

/** 변경 성공 시 리스트 보기 캐시도 함께 무효화 */
const INVALIDATE = { invalidateKeys: [[SCHEDULE_LIST_KEY]] } as const

interface Props {
  taskId: number
  siteId: number
  scheduleId: number
  /** 딥링크 card=cr — 값이 있으면 카드를 화면에 보이게 스크롤(같은 공종에 다시 들어오면 값이 바뀜). 없으면 undefined */
  expandNonce?: number
  /** 받는 반장님 이름(공정의 현재 반장) — 추가 전달 확인창 표시용 */
  foremanName?: string
  onChanged?: () => void
}

export function CustReqCard({ taskId, siteId, scheduleId, expandNonce, foremanName, onChanged }: Props) {
  const list = useCustReqList(taskId)
  const srcQuery = useCustReqSrcOptions()
  const createM = useCreateCustReq(INVALIDATE)
  const updateM = useUpdateCustReq(INVALIDATE)
  const deleteM = useDeleteCustReq(INVALIDATE)
  const addPhotosM = useAddCustReqPhotos(INVALIDATE)
  const removePhotoM = useDeleteCustReqPhoto(INVALIDATE)

  const rootRef = useRef<HTMLDivElement>(null)
  const [adding, setAdding] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [deleting, setDeleting] = useState<CustReqItem | null>(null)
  const [showChecked, setShowChecked] = useState(false)
  const [saving, setSaving] = useState(false)

  // 다른 공종으로 바뀌면 열려 있던 입력·확인창을 닫는다
  useEffect(() => {
    setAdding(false)
    setEditingId(null)
    setDeleting(null)
    setShowChecked(false)
  }, [taskId])
  useEffect(() => {
    if (expandNonce !== undefined) rootRef.current?.scrollIntoView({ block: 'nearest' })
  }, [expandNonce])

  const srcOptions = srcQuery.isError
    ? ({ status: 'error', retry: () => { void srcQuery.refetch() } } as const)
    : srcQuery.data
      ? ({ status: 'ready', options: srcQuery.data } as const)
      : ({ status: 'loading' } as const)

  const notifyChanged = () => { onChanged?.() }

  const submitCreate = async (v: CustReqFormValue) => {
    setSaving(true)
    try {
      await createM.mutateAsync({ siteId, scheduleId, taskId, requestKey: v.requestKey, content: v.content, srcCode: v.srcCode, files: v.newFiles })
      toast.success(CUST_REQ_MESSAGE.saved)
      setAdding(false)
      notifyChanged()
    } catch (e) {
      // 입력은 그대로 둔다 — 폴백 값으로 저장하지 않음
      toast.error(custReqErrorMessage(e, CUST_REQ_MESSAGE.saveFail))
    } finally { setSaving(false) }
  }

  const submitEdit = async (item: CustReqItem, v: CustReqFormValue) => {
    setSaving(true)
    let changed = false
    try {
      if (v.content !== item.content || v.srcCode !== item.srcCode) {
        await updateM.mutateAsync({ custReqId: item.custReqId, input: { content: v.content, srcCode: v.srcCode } })
        changed = true
      }
      for (const photoId of v.removedPhotoIds) {
        if (!item.photos.some(p => p.photoId === photoId)) continue
        await removePhotoM.mutateAsync({ custReqId: item.custReqId, photoId })
        changed = true
      }
      if (v.newFiles.length > 0) {
        await addPhotosM.mutateAsync({ custReqId: item.custReqId, files: v.newFiles })
        changed = true
      }
      toast.success(CUST_REQ_MESSAGE.updated)
      setEditingId(null)
    } catch (e) {
      toast.error(custReqErrorMessage(e, CUST_REQ_MESSAGE.saveFail))
    } finally {
      setSaving(false)
      if (changed) notifyChanged()
    }
  }

  const confirmDelete = async () => {
    if (!deleting) return
    try {
      await deleteM.mutateAsync(deleting.custReqId)
      toast.success(CUST_REQ_MESSAGE.deleted)
      setDeleting(null)
      notifyChanged()
    } catch (e) {
      toast.error(custReqErrorMessage(e, CUST_REQ_MESSAGE.deleteFail))
      setDeleting(null)
    }
  }

  const { active, checked } = splitByChecked(list.data ?? [])

  return (
    <div id={CUST_REQ_CARD_ANCHOR_ID} ref={rootRef} onMouseDown={e => e.stopPropagation()}>
      <TaskDetailCard
        title={CUST_REQ_MESSAGE.title}
        emphasis
        tag={!adding && (
          <span className="ml-auto flex items-center gap-1.5">
            {editingId === null && (
              <CustReqAdditionalDelivery
                taskId={taskId}
                targets={pickUnsentItems(list.data ?? [])}
                foremanName={foremanName}
                invalidate={INVALIDATE}
                onDelivered={notifyChanged}
                onConflict={() => { void list.refetch(); notifyChanged() }}
              />
            )}
            <Button variant="secondary" size="sm" onClick={() => { setEditingId(null); setAdding(true) }}>
              {CUST_REQ_MESSAGE.add}
            </Button>
          </span>
        )}
      >
        {adding && (
          <CustReqForm
            srcOptions={srcOptions}
            submitting={saving}
            onSubmit={submitCreate}
            onCancel={() => setAdding(false)}
          />
        )}

        {list.isLoading && (
          <div aria-busy="true" className="flex flex-col gap-1.5">
            {[0, 1].map(i => <Skeleton key={i} height={24} />)}
          </div>
        )}
        {list.isError && <ErrorState title={CUST_REQ_MESSAGE.loadError} onRetry={() => { void list.refetch() }} />}
        {list.data && list.data.length === 0 && !adding && (
          <EmptyState title={CUST_REQ_MESSAGE.empty} description={CUST_REQ_MESSAGE.emptyHint} />
        )}

        {active.length > 0 && (
          <ul className="m-0 p-0">
            {active.map(it => (
              editingId === it.custReqId
                ? (
                  <li key={it.custReqId} style={{ listStyle: 'none' }}>
                    <CustReqForm
                      initial={it}
                      srcOptions={srcOptions}
                      submitting={saving}
                      onSubmit={v => { void submitEdit(it, v) }}
                      onCancel={() => setEditingId(null)}
                    />
                  </li>
                )
                : (
                  <CustReqItemRow
                    key={it.custReqId}
                    item={it}
                    onEdit={x => { setAdding(false); setEditingId(x.custReqId) }}
                    onDelete={setDeleting}
                  />
                )
            ))}
          </ul>
        )}

        {checked.length > 0 && (
          <div className="mt-1">
            <TextLink aria-expanded={showChecked} onClick={() => setShowChecked(s => !s)}>
              {CUST_REQ_MESSAGE.checkedToggle(checked.length)}
            </TextLink>
            {showChecked && (
              <ul className="m-0 p-0 mt-1">
                {checked.map(it => <CustReqItemRow key={it.custReqId} item={it} onEdit={() => undefined} onDelete={() => undefined} />)}
              </ul>
            )}
          </div>
        )}
      </TaskDetailCard>

      <Modal
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        title={CUST_REQ_MESSAGE.removeConfirmTitle}
        size="sm"
        footer={(
          <>
            <Button variant="secondary" onClick={() => setDeleting(null)}>{CUST_REQ_MESSAGE.cancel}</Button>
            <Button variant="danger" loading={deleteM.isPending} onClick={() => { void confirmDelete() }}>{CUST_REQ_MESSAGE.removeConfirmOk}</Button>
          </>
        )}
      >
        <p>{CUST_REQ_MESSAGE.removeConfirmBody}</p>
        {deleting && <p className="mt-1" style={{ color: 'var(--shell-on-surface-2)' }}>{deleting.content.length > 60 ? `${deleting.content.slice(0, 60)}…` : deleting.content}</p>}
      </Modal>
    </div>
  )
}
